import moment, { Moment } from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';

export type StaySelectionErrorKey =
    | 'rooms_page_error_invalid_move_in_date'
    | 'rooms_page_error_invalid_stay_duration';

interface ValidateStaySelectionParams {
    moveInDate: string | null;
    duration: string | null;
    // The development's last move out date
    moveOutDate?: string;
    minimumStayDuration: number;
    // The stay's own move out date (YYYY-MM-DD), given instead of a duration
    requestedMoveOutDate?: string | null;
}

// Rooms can't be booked to move in today, so the earliest move in is tomorrow
export const getEarliestMoveInDate = () => moment().add(1, 'day').startOf('day');

export const getMaximumStayDuration = (moveInDate: Moment, moveOutDate: Moment) =>
    moveOutDate.diff(moveInDate, 'week');

export const validateStaySelection = ({
    moveInDate,
    duration,
    moveOutDate,
    minimumStayDuration,
    requestedMoveOutDate,
}: ValidateStaySelectionParams): StaySelectionErrorKey | null => {
    if (!moveInDate || !moveOutDate || (!duration && !requestedMoveOutDate)) {
        return null;
    }

    const parsedMoveInDate = moment(moveInDate, DATE_FORMAT, true);
    const parsedMoveOutDate = moment(moveOutDate);

    if (
        !parsedMoveInDate.isValid() ||
        !parsedMoveOutDate.isValid() ||
        parsedMoveInDate.isBefore(getEarliestMoveInDate(), 'day')
    ) {
        return 'rooms_page_error_invalid_move_in_date';
    }

    if (requestedMoveOutDate) {
        const parsedRequestedMoveOutDate = moment(requestedMoveOutDate, DATE_FORMAT, true);
        // Can include part weeks, which are charged pro rata
        const stayWeeks = parsedRequestedMoveOutDate.diff(parsedMoveInDate, 'days') / 7;

        if (
            !parsedRequestedMoveOutDate.isValid() ||
            parsedRequestedMoveOutDate.isAfter(parsedMoveOutDate, 'day') ||
            stayWeeks < minimumStayDuration
        ) {
            return 'rooms_page_error_invalid_stay_duration';
        }

        return null;
    }

    const maximumStayDuration = getMaximumStayDuration(parsedMoveInDate, parsedMoveOutDate);
    const stayDuration = Number(duration);

    if (
        !Number.isInteger(stayDuration) ||
        stayDuration < minimumStayDuration ||
        stayDuration > maximumStayDuration
    ) {
        return 'rooms_page_error_invalid_stay_duration';
    }

    return null;
};
